import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

/**
 * MetricLineChart
 * A responsive, accessible D3 + React line chart in a dark theme with cyan/blue/purple accents.
 *
 * Props:
 *  - data: Array<{ timestamp: string | Date, [value_field]: number }>
 *  - title?: string
 *  - height?: number (default 320)
 *  - smoothness?: number (0 = straight line, 1 = smoothest)
 *  - onTimeWindowChange?: function (timeWindow: string) => void
 *  - initialValue?: number  — Portfolio starting balance. When data has < 2 points, a synthetic
 *                             "pre-data" line is drawn from this value to currentValue so there
 *                             is always something visible before snapshots accumulate.
 *  - currentValue?: number — Current portfolio value (used for pre-data line endpoint).
 */
const METRIC_TABS = [
  { label: "Account Value", field: "equity" },
  { label: "PNL", field: "total_pnl" },
];

export default function MetricLineChart({
  data: raw = DEFAULT_DATA,
  value_field = "equity",
  title = null,
  height: _height = 320,
  smoothness = 0,
  // eslint-disable-next-line no-unused-vars
  timeWindow = "24H",
  // eslint-disable-next-line no-unused-vars
  onTimeWindowChange = null,
  initialValue = null,
  currentValue = null,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [activeTab, setActiveTab] = useState(
    METRIC_TABS.find((t) => t.field === value_field)?.field ?? METRIC_TABS[0].field
  );
  const activeField = activeTab;

  const isMobile = width < 480;
  const effectiveHeight = isMobile ? 180 : 320;

  // Handle responsive width using ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width);
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const calcTimezoneOffsetUTC = (timezone) => {
    // given a timezone name like "America/Los_Angeles", return offset in hours from UTC
    // Always uses UTC midnight as the reference
    try {
      // Use a fixed UTC date (midnight Jan 2, 2000 avoids DST ambiguity)
      const utcDate = new Date(Date.UTC(2000, 0, 2, 0, 0, 0));
      // Get the hour in UTC
      const utcHour = utcDate.getUTCHours();
      // Get the hour in the target timezone
      const tzParts = utcDate.toLocaleString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split(/\D+/);
      // tzParts: [MM, DD, YYYY, HH, MM, SS]
      const tzHour = parseInt(tzParts[3], 10);
      // Calculate the offset
      let offset = tzHour - utcHour;
      // Adjust for day wrap (e.g., -16 becomes +8)
      if (offset <= -12) offset += 24;
      if (offset > 12) offset -= 24;
      // console.log(`Timezone offset for ${timezone}: ${offset} hours`);
      return -offset;
    } catch (e) {
      // Fallback: return 0 if timezone is invalid
      return 0;
    }
  };

  // Synthesise two phantom data points when real snapshots haven't accumulated yet.
  // The line runs from initialValue (or currentValue) at now-1h to currentValue at now,
  // visually anchoring the chart at the portfolio's current level.
  const { data, isPreData } = useMemo(() => {
    const real = raw
      .map((d) => ({
        date: d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp),
        value: +d[activeField],
      }))
      .sort((a, b) => a.date - b.date);

    if (real.length >= 2) return { data: real, isPreData: false };

    // Not enough real snapshots — build phantom data
    const cv = currentValue ?? (real.length === 1 ? real[0].value : null);
    const iv = initialValue ?? cv;
    if (cv == null) return { data: real, isPreData: false };

    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const phantom = [
      { date: oneHourAgo, value: iv },
      { date: now,        value: cv },
    ];
    return { data: phantom, isPreData: true };
  }, [raw, activeField, initialValue, currentValue]);

  const margin = isMobile
    ? { top: 16, right: 8, bottom: 28, left: 44 }
    : { top: 28, right: 24, bottom: 36, left: 56 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, effectiveHeight - margin.top - margin.bottom);

  const [x, y, linePath, ticksX, ticksY, yFormat] = useMemo(() => {
    const dates = data.map((d) => d.date);
    const values = data.map((d) => d.value); // Fixed: use d.value instead of d[value_field]

    const x = d3
      .scaleUtc()
      .domain(d3.extent(dates))
      .range([0, innerW]);

    // Calculate buffer. When range === 0 (flat line / single value), use 1% of the
    // value as buffer so the scale doesn't collapse and the line lands at mid-chart.
    const minValue = d3.min(values) ?? 0;
    const maxValue = d3.max(values) ?? 1;
    const range = maxValue - minValue;
    const buffer = range === 0
      ? Math.max(Math.abs(minValue) * 0.01, 50)
      : range * 0.1;
    
    const y = d3
      .scaleLinear()
      .domain([minValue - buffer, maxValue + buffer])
      .range([innerH, 0]);

    const curveType = smoothness <= 0 ? d3.curveLinear : smoothness >= 1 ? d3.curveMonotoneX : d3.curveCatmullRom.alpha(smoothness);

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value)) // Fixed: use d.value instead of d[value_field]
      .curve(curveType);

    const ticksX = x.ticks(isMobile ? Math.min(4, Math.floor(innerW / 80)) : Math.min(8, Math.floor(innerW / 100)));
    const ticksY = y.ticks(isMobile ? 4 : 6);
    const yFormat = d3.format(".2f");

    return [x, y, line(data), ticksX, ticksY, yFormat];
  }, [data, innerW, innerH, smoothness]);

  // Tooltip state
  const [hover, setHover] = useState(null);
  const bisect = d3.bisector((d) => d.date).center;

  function onMouseMove(e) {
    const { left } = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - left - margin.left;
    const date = x.invert(px);
    const idx = bisect(data, date);
    const d = data[Math.max(0, Math.min(data.length - 1, idx))];
    setHover(d);
  }

  function onMouseLeave() {
    setHover(null);
  }



  return (
    <div
      ref={containerRef}
      className={`w-full bg-zinc-900 text-zinc-200 rounded-2xl shadow-xl border border-zinc-800 ${isMobile ? "p-2" : "p-4"}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {title && (
            <h2 className="text-lg font-semibold tracking-tight text-cyan-300 mt-0">
              {title}
            </h2>
          )}
          <div className="flex gap-1">
            {METRIC_TABS.map((tab) => (
              <button
                key={tab.field}
                onClick={() => setActiveTab(tab.field)}
                className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                  activeTab === tab.field
                    ? "bg-zinc-700 text-cyan-300 border border-zinc-600"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {!isMobile && data?.length ? (
          <span className="text-xs text-zinc-400">
            {d3.timeFormat("%b %d, %Y")(data[0].date)} — {" "}
            {d3.timeFormat("%b %d, %Y")(data[data.length - 1].date)}
          </span>
        ) : null}
      </div>

      <svg
        role="img"
        aria-label={`${title} line chart`}
        viewBox={`0 0 ${width} ${effectiveHeight}`}
        className="w-full h-auto select-none"
      >
        <defs>
          {/* Stroke gradient: cyan → blue → purple */}
          <linearGradient id="lineGradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
          {/* Area fade */}
          <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.22" />
            <stop offset="60%" stopColor="#60a5fa" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* X grid */}
          {ticksX.map((t, i) => (
            <line
              key={`xg-${i}`}
              x1={x(t)}
              x2={x(t)}
              y1={0}
              y2={innerH}
              className="stroke-zinc-800"
              strokeDasharray="2,4"
            />
          ))}
          {/* Y grid */}
          {ticksY.map((t, i) => (
            <line
              key={`yg-${i}`}
              x1={0}
              x2={innerW}
              y1={y(t)}
              y2={y(t)}
              className="stroke-zinc-800"
              strokeDasharray="2,4"
            />
          ))}

          {/* Area under line */}
          <path
            d={d3.area()
              .x((d) => x(d.date))
              .y0(innerH)
              .y1((d) => y(d.value))
              .curve(smoothness <= 0 ? d3.curveLinear : smoothness >= 1 ? d3.curveMonotoneX : d3.curveCatmullRom.alpha(smoothness))(data)}
            fill={isPreData ? "none" : "url(#areaGradient)"}
          />

          {/* Line — dashed + muted in pre-data mode */}
          <path
            d={linePath || ""}
            fill="none"
            stroke={isPreData ? "#94a3b8" : "url(#lineGradient)"}
            strokeWidth={isPreData ? 1.75 : 2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray={isPreData ? "6 4" : undefined}
            opacity={isPreData ? 0.55 : 1}
          />
          {/* Pulsing endpoint dot in pre-data mode */}
          {isPreData && data.length > 0 && (
            <circle
              cx={x(data[data.length - 1].date)}
              cy={y(data[data.length - 1].value)}
              r={4}
              fill="#94a3b8"
              opacity={0.7}
            >
              <animate attributeName="opacity" values="0.7;0.2;0.7" dur="2s" repeatCount="indefinite" />
              <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
            </circle>
          )}

          {/* Axes */}
          <g transform={`translate(0,${innerH})`}>
            {ticksX.map((t, i) => (
              <g key={`xt-${i}`} transform={`translate(${x(t)},0)`}>
                <line y2="6" className="stroke-zinc-700" />
                <text
                  className="fill-zinc-400 text-[10px]"
                  textAnchor="middle"
                  dy="0.9em"
                >
                  {(() => {
                    // Hardcoded -7 hour offset for local time
                    const local = new Date(t.getTime() - (calcTimezoneOffsetUTC("America/Los_Angeles") * 60 * 60 * 1000));
                    return d3.timeFormat("%b %d %H:%M")(local);
                  })()}
                </text>
              </g>
            ))}
          </g>

          <g>
            {ticksY.map((t, i) => (
              <g key={`yt-${i}`} transform={`translate(0,${y(t)})`}>
                <line x2="-6" className="stroke-zinc-700" />
                <text
                  className="fill-zinc-400 text-[10px]"
                  textAnchor="end"
                  dx="-0.5em"
                  dy="0.32em"
                >
                  {yFormat(t)}
                </text>
              </g>
            ))}
          </g>

          <rect
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          />

          {hover && (
            <g transform={`translate(${x(hover.date)},${y(hover.value)})`}>
              <circle r={4} fill="#22d3ee" stroke="#0ea5b7" strokeWidth={1.5} />
              <foreignObject x={8} y={-36} width={200} height={48}>
                <div className="px-2 py-1 rounded-md bg-zinc-800/90 border border-zinc-700 shadow-lg">
                  <div className="text-[10px] leading-tight text-zinc-400">
                    {(() => {
                      // Convert UTC to local time for hover display
                      const local = new Date(hover.date.getTime() - (calcTimezoneOffsetUTC("America/Los_Angeles") * 60 * 60 * 1000));
                      return d3.timeFormat("%b %d, %Y %H:%M")(local);
                    })()}
                  </div>
                  <div className="text-sm font-semibold text-cyan-300">
                    {yFormat(hover.value)}
                  </div>
                </div>
              </foreignObject>
            </g>
          )}
        </g>
      </svg>

      {isPreData && (
        <div className="mt-2 flex items-center gap-2 px-1">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-500 animate-pulse" />
            Collecting snapshot data — chart will update automatically
          </span>
        </div>
      )}

      {!isMobile && !isPreData && (
        <div className="mt-3 flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-2 text-zinc-400">
            <span className="h-2 w-6 rounded-full" style={{ background: "linear-gradient(90deg,#22d3ee,#60a5fa,#a78bfa)" }} />
            Series: {METRIC_TABS.find((t) => t.field === activeField)?.label ?? activeField}
          </span>
        </div>
      )}
    </div>
  );
}

const DEFAULT_DATA = [
    {
      "timestamp": 1764690349428,
      "equity": 10091.444726901369,
      "margin_used": 109.54568657478308,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764693949428,
      "equity": 10098.481192785333,
      "margin_used": 110.26033690658501,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764697549428,
      "equity": 10097.860241619775,
      "margin_used": 110.20929045431345,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764701149428,
      "equity": 10093.666596052311,
      "margin_used": 109.80091883614091,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764704749428,
      "equity": 10071.097615279818,
      "margin_used": 107.55487493619194,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764708349428,
      "equity": 10075.583905904283,
      "margin_used": 108.01429300663607,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764711949428,
      "equity": 10089.768428619946,
      "margin_used": 109.44359367023995,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764715549428,
      "equity": 10080.983173387684,
      "margin_used": 108.5758039816233,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764719149428,
      "equity": 10081.385068912603,
      "margin_used": 108.62685043389486,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764722749428,
      "equity": 10081.786661561931,
      "margin_used": 108.67789688616642,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764726349428,
      "equity": 10092.907635698402,
      "margin_used": 109.80091883614091,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764729949428,
      "equity": 10094.329358516166,
      "margin_used": 109.95405819295561,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764733549428,
      "equity": 10097.792647609238,
      "margin_used": 110.31138335885657,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764737149428,
      "equity": 10101.76546707494,
      "margin_used": 110.71975497702911,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764740749428,
      "equity": 10101.656244682568,
      "margin_used": 110.71975497702911,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764744349428,
      "equity": 10100.014194316733,
      "margin_used": 110.5666156202144,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764747949428,
      "equity": 10100.924497192345,
      "margin_used": 110.66870852475755,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764751549428,
      "equity": 10101.323883784149,
      "margin_used": 110.71975497702911,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764755149428,
      "equity": 10099.171123872598,
      "margin_used": 110.51556916794284,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764758749428,
      "equity": 10098.039938744123,
      "margin_used": 110.41347626339972,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764762349428,
      "equity": 10090.783086608673,
      "margin_used": 109.69882593159778,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764765949428,
      "equity": 10096.79849498028,
      "margin_used": 110.31138335885657,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764769549428,
      "equity": 10099.753848902346,
      "margin_used": 110.61766207248598,
      "total_pnl": 0,
      "cash_balance": 0
    },
    {
      "timestamp": 1764773149428,
      "equity": 10088.413916964275,
      "margin_used": 109.4946401225115,
      "total_pnl": 0,
      "cash_balance": 0
    }
  ];
